import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PHONE_NUMBER_IMASK_PROPS,
  PHONE_NUMBER_PLACEHOLDER,
  SUPPORT_PHONE,
  SUPPORT_WEBSITE_LABEL,
  SUPPORT_WEBSITE_URL,
} from '@/constants/app';
import { Button, Input, Stack, Text, TextInput, Textarea, Title } from '@mantine/core';
import { isEmail, isNotEmpty, useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { IMaskInput } from 'react-imask';
import { IconCheck, IconPhone, IconWorldWww, IconX } from '@tabler/icons-react';
import logoMpa from '@/assets/logo-mpa.svg';
import { GuardedModal } from '@/components/modals/GuardedModal/GuardedModal';
import { GuardedModalCloseActionIcon } from '@/components/modals/GuardedModal/GuardedModalCloseControls';
import { MessageFeedbackWithAction } from '@/components/MessageFeedbackWithAction/MessageFeedbackWithAction';
import { formatPhoneNumber, isValidPhoneNumberValue } from '@/helpers';
import { useChurch, useContactFormMutation, useCurrentUserProfile, useGlobalSettings, useScreenSize } from '@/hooks';
import { type Church, GRADIENT_ICON_VARIANT, type ScopeListedItem, type UserProfileResponse } from '@/types';
import styles from './SupportPad.module.sass';

const CONTACT_INPUT_WRAPPER_ORDER: ('label' | 'input' | 'error')[] = ['label', 'input', 'error'];

type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  zip: string;
  parishName: string;
  description: string;
};

const ZIP_CODE_LENGTH = 5;
const ZIP_CODE_PATTERN = /^\d{5}$/;

const getContactFormValues = (
  userProfile: UserProfileResponse | undefined,
  currentChurch: ScopeListedItem | null | undefined,
  church: Church | undefined
): ContactFormData => {
  const churchId = currentChurch?.churchId ?? '';
  const registrationZip = userProfile?.registrations?.find((registration) => registration.id === churchId)?.zipCode;

  return {
    firstName: userProfile?.firstName ?? '',
    lastName: userProfile?.lastName ?? '',
    email: userProfile?.email ?? '',
    phoneNumber: formatPhoneNumber(church?.phoneNumber),
    zip: (currentChurch?.zipCode ?? registrationZip ?? '').slice(0, ZIP_CODE_LENGTH),
    parishName: currentChurch?.churchName ?? '',
    description: '',
  };
};

export const SupportPad = () => {
  const { t } = useTranslation('global');
  const { isMobile } = useScreenSize();
  const { currentChurch } = useGlobalSettings();
  const { data: userProfile } = useCurrentUserProfile();
  const { data: church } = useChurch(currentChurch?.churchId);
  const useMobilePadCopy = isMobile === true;
  const [isModalOpen, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [isSuccessFeedbackVisible, setIsSuccessFeedbackVisible] = useState(false);
  const { mutate: submitContactForm } = useContactFormMutation();
  const churchId = currentChurch?.churchId ?? '';

  const contactFormDefaults = useMemo(
    () => getContactFormValues(userProfile, currentChurch, church),
    [userProfile, currentChurch, church]
  );

  const form = useForm<ContactFormData>({
    initialValues: contactFormDefaults,
    validate: {
      firstName: isNotEmpty(t('forms.requiredText')),
      lastName: isNotEmpty(t('forms.requiredText')),
      email: (value) => {
        if (!value) return t('forms.requiredText');
        return isEmail(t('forms.invalidEmail'))(value);
      },
      phoneNumber: (value) => {
        if (!value) return t('forms.requiredText');
        if (!isValidPhoneNumberValue(value)) {
          return t('forms.phoneMinLength');
        }
        return null;
      },
      zip: (value) => {
        if (!value) return t('forms.requiredText');
        if (!ZIP_CODE_PATTERN.test(value)) {
          return t('support.modal.form.invalidZipCode');
        }
        return null;
      },
      description: isNotEmpty(t('forms.requiredText')),
    },
  });

  const handleFormSubmit = () => {
    const { hasErrors } = form.validate();
    if (hasErrors || !churchId) return;

    submitContactForm(
      {
        churchId,
        data: {
          firstName: form.values.firstName,
          lastName: form.values.lastName,
          email: form.values.email,
          phoneNumber: form.values.phoneNumber,
          zip: Number(form.values.zip),
          parishName: form.values.parishName,
          description: form.values.description,
        },
      },
      {
        onSuccess: () => setIsSuccessFeedbackVisible(true),
      }
    );
  };

  const handleModalOpen = () => {
    form.setValues(contactFormDefaults);
    setIsSuccessFeedbackVisible(false);
    openModal();
  };

  const handleModalClose = () => {
    form.setValues(contactFormDefaults);
    setIsSuccessFeedbackVisible(false);
    closeModal();
  };

  return (
    <div className={styles.supportPad}>
      <div className={styles.supportPadHeader}>
        <Title mb={4} order={6} className={styles.supportTitle}>
          {t('support.title')}
        </Title>
        <Text size="lg" className={styles.supportSubtitle}>
          {t(useMobilePadCopy ? 'support.mobile.description' : 'support.description')}
        </Text>
      </div>

      <div className={styles.supportPadCard}>
        <Text component="div" size="md" className={styles.contactRow}>
          <IconPhone size={18} stroke={1.5} className={styles.contactRowIcon} />
          <a href={`tel:${SUPPORT_PHONE}`}>{SUPPORT_PHONE}</a>
        </Text>
        <Text component="div" size="md" className={styles.contactRow}>
          <IconWorldWww size={18} stroke={1.5} className={styles.contactRowIcon} />
          <a href={SUPPORT_WEBSITE_URL} target="_blank" rel="noreferrer">
            {SUPPORT_WEBSITE_LABEL}
          </a>
        </Text>
        <Button variant="outline" color="brand" size="sm" onClick={handleModalOpen} className={styles.contactButton}>
          {t(useMobilePadCopy ? 'support.mobile.ctaLabel' : 'support.sendMessageButtonLabel')}
        </Button>
      </div>

      <GuardedModal
        opened={isModalOpen}
        onClose={handleModalClose}
        hasUnsavedChanges={!isSuccessFeedbackVisible && form.isDirty()}
        fullScreen={isMobile === true}
        size={isMobile === true ? undefined : 1170}
        withCloseButton={false}
        padding={0}
        radius={isMobile === true ? 0 : 'xl'}
        classNames={{ body: styles.modalBody, content: styles.modalRoot, inner: styles.modalInner }}
      >
        <div className={styles.modalContent}>
          <div className={styles.modalLeft}>
            {isMobile !== true && <div className={styles.modalLeftSpacer} aria-hidden />}
            <div className={styles.logoWrapper}>
              <img src={logoMpa} alt="myParish" width={278} className={styles.footerLogo} />
            </div>
            <div className={styles.contactInfo}>
              <Text size={isMobile === true ? 'sm' : 'xl'} c="white" className={styles.phoneText}>
                <IconPhone size={isMobile === true ? 18 : 24} className={styles.phoneIcon} />
                <a style={{ color: 'white' }} href={`tel:${SUPPORT_PHONE}`}>
                  {SUPPORT_PHONE}
                </a>
              </Text>
              <Text size={isMobile === true ? 'sm' : 'xl'} c="white" className={styles.phoneText}>
                <IconWorldWww size={isMobile === true ? 18 : 24} className={styles.phoneIcon} />
                <a style={{ color: 'white' }} href={SUPPORT_WEBSITE_URL} target="_blank" rel="noreferrer">
                  {SUPPORT_WEBSITE_LABEL}
                </a>
              </Text>
            </div>
          </div>
          <div className={styles.modalRight}>
            <GuardedModalCloseActionIcon
              variant="subtle"
              color="gray"
              size="xl"
              className={styles.modalCloseButton}
              aria-label={t('closeButtonLabel')}
            >
              <IconX size={20} />
            </GuardedModalCloseActionIcon>
            {isSuccessFeedbackVisible ? (
              <div className={styles.feedbackCenter}>
                <MessageFeedbackWithAction
                  icon={<IconCheck size={40} />}
                  title={t('support.modal.successTitle')}
                  text={t('support.modal.successText')}
                  actions={[]}
                  iconVariant={GRADIENT_ICON_VARIANT.GREEN}
                  compact
                />
              </div>
            ) : (
              <div>
                <Title order={4} c="base.8" fw={500} className={styles.modalTitle}>
                  {t('support.modal.title')}
                </Title>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleFormSubmit();
                  }}
                  className={styles.contactForm}
                >
                  <Stack gap="md">
                    <TextInput
                      label={t('support.modal.form.firstName')}
                      placeholder={t('support.modal.form.firstNamePlaceholder')}
                      required
                      size="md"
                      {...form.getInputProps('firstName')}
                      inputWrapperOrder={CONTACT_INPUT_WRAPPER_ORDER}
                    />
                    <TextInput
                      label={t('support.modal.form.lastName')}
                      placeholder={t('support.modal.form.lastNamePlaceholder')}
                      required
                      size="md"
                      {...form.getInputProps('lastName')}
                      inputWrapperOrder={CONTACT_INPUT_WRAPPER_ORDER}
                    />
                    <TextInput
                      label={t('support.modal.form.email')}
                      placeholder={t('support.modal.form.emailPlaceholder')}
                      required
                      size="md"
                      type="email"
                      {...form.getInputProps('email')}
                      inputWrapperOrder={CONTACT_INPUT_WRAPPER_ORDER}
                    />
                    <Input.Wrapper
                      label={t('support.modal.form.phoneNumber')}
                      required
                      error={form.errors.phoneNumber}
                      inputWrapperOrder={CONTACT_INPUT_WRAPPER_ORDER}
                    >
                      <Input
                        component={IMaskInput}
                        {...PHONE_NUMBER_IMASK_PROPS}
                        placeholder={PHONE_NUMBER_PLACEHOLDER}
                        value={form.values.phoneNumber}
                        unmask={false}
                        size="md"
                        onAccept={(value) => form.setFieldValue('phoneNumber', value)}
                      />
                    </Input.Wrapper>
                    <TextInput
                      label={t('support.modal.form.zipCode')}
                      placeholder={t('support.modal.form.zipCodePlaceholder')}
                      required
                      size="md"
                      maxLength={ZIP_CODE_LENGTH}
                      {...form.getInputProps('zip')}
                      inputWrapperOrder={CONTACT_INPUT_WRAPPER_ORDER}
                    />
                    <TextInput
                      label={t('support.modal.form.parishName')}
                      placeholder={t('support.modal.form.parishNamePlaceholder')}
                      size="md"
                      {...form.getInputProps('parishName')}
                      inputWrapperOrder={CONTACT_INPUT_WRAPPER_ORDER}
                    />
                    <Textarea
                      label={t('support.modal.form.message')}
                      placeholder={t('support.modal.form.messagePlaceholder')}
                      required
                      size="md"
                      autosize
                      minRows={3}
                      {...form.getInputProps('description')}
                      inputWrapperOrder={CONTACT_INPUT_WRAPPER_ORDER}
                    />
                  </Stack>

                  <div className={styles.submitRow}>
                    <Button type="submit" size="md" fullWidth={isMobile === true}>
                      {t('support.sendMessageButtonLabel')}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </GuardedModal>
    </div>
  );
};
